/**
 * The form controls, with the two rules that belong in a component rather than in a checklist.
 *
 * RULE ONE — `withAsterisk`, NEVER `required`. Mantine's docs distinguish them: `required` also
 * adds the native HTML attribute, which makes the BROWSER block submission before our own
 * validation runs, producing a second unstyled error channel that matches nothing else on the
 * page. Nothing here ever sets `required`.
 *
 * RULE TWO — the label, the description and the error must be LINKED to the control by ARIA. The
 * brand repo's own field component is presentational only: its comment tells the caller to wire
 * the label by hand, and the error text is linked to nothing at all. Mantine's `TextInput` and
 * `Textarea` close that for free by building on `Input.Wrapper`, which is why these wrap those
 * rather than composing labels out of `<Text>`.
 *
 * THE COUNTER SHOWS THE RULED NUMBER, which is a GRAPHEME count — one emoji is one character, the
 * way a person counts. The database CHECK sits about ten per cent higher as a backstop and is
 * deliberately not published; telling somebody the backstop invites them to send text the API
 * refuses.
 */
import { Group, Stack, Switch, Text, TextInput, Textarea } from '@mantine/core';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { useTranslation } from '../../../i18n';
import { graphemeLength } from '../domain/limits';
import { defined } from './defined';

interface CommonFieldProps {
  readonly id: string;
  readonly label: string;
  readonly description?: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly error?: string | undefined;
  /** Marks the control with an asterisk. NEVER becomes the native `required` attribute. */
  readonly requiredToSubmit?: boolean;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly maxCount?: number | undefined;
  readonly minCount?: number | undefined;
  /** Rendered under the control — the per-field visibility switch, when the field has one. */
  readonly footer?: ReactNode;
}

/**
 * The counter, and the one place the bio's BAND is explained before somebody starts typing.
 * A minimum that is only discovered on save is a minimum that wastes an afternoon.
 */
function Counter({
  value,
  maxCount,
  minCount,
}: {
  readonly value: string;
  readonly maxCount: number | undefined;
  readonly minCount: number | undefined;
}): ReactNode {
  const { t } = useTranslation('application');
  if (maxCount === undefined && minCount === undefined) return null;
  const count = graphemeLength(value);
  const shortfall = minCount !== undefined && count > 0 && count < minCount ? minCount - count : 0;
  const under = shortfall > 0;
  const over = maxCount !== undefined && count > maxCount;
  const tone = under || over ? 'oklch(var(--mx-danger))' : undefined;

  return (
    // Never broken or squeezed: beside a switch in a half-width column "0 / 40" fell onto two lines.
    <Text
      size="xs"
      style={{
        whiteSpace: 'nowrap',
        flexShrink: 0,
        ...(tone === undefined ? {} : { color: tone }),
      }}
      {...defined({ c: tone === undefined ? ('dimmed' as const) : undefined })}
    >
      {minCount !== undefined && maxCount !== undefined
        ? `${count} / ${minCount}–${maxCount}`
        : `${count} / ${maxCount ?? minCount ?? 0}`}
      {under ? ` ${t('fields.moreToGo', { count: shortfall })}` : ''}
    </Text>
  );
}

export function TextField(props: CommonFieldProps): ReactNode {
  const {
    id,
    label,
    description,
    value,
    onChange,
    error,
    requiredToSubmit,
    placeholder,
    disabled,
    maxCount,
    minCount,
    footer,
  } = props;
  return (
    <Stack gap={6} id={id}>
      <TextInput
        label={label}
        value={value}
        withAsterisk={requiredToSubmit === true}
        {...defined({ description, placeholder, disabled, error })}
        onChange={(event) => {
          onChange(event.currentTarget.value);
        }}
      />
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <div style={{ minWidth: 0 }}>{footer}</div>
        <Counter value={value} maxCount={maxCount} minCount={minCount} />
      </Group>
    </Stack>
  );
}

export interface TextAreaFieldProps extends CommonFieldProps {
  readonly minRows?: number;
}

export function TextAreaField(props: TextAreaFieldProps): ReactNode {
  const {
    id,
    label,
    description,
    value,
    onChange,
    error,
    requiredToSubmit,
    placeholder,
    disabled,
    maxCount,
    minCount,
    footer,
    minRows = 4,
  } = props;
  return (
    <Stack gap={6} id={id}>
      <Textarea
        label={label}
        value={value}
        withAsterisk={requiredToSubmit === true}
        autosize
        minRows={minRows}
        {...defined({ description, placeholder, disabled, error })}
        onChange={(event) => {
          onChange(event.currentTarget.value);
        }}
      />
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <div style={{ minWidth: 0 }}>{footer}</div>
        <Counter value={value} maxCount={maxCount} minCount={minCount} />
      </Group>
    </Stack>
  );
}

/**
 * One of the SEVEN per-field visibility switches. There are exactly seven and they do not cover
 * every field: `displayName`, `avatar`, `bioShort` and `about` have NO switch, and that absence is
 * the contract rather than an oversight. Everything defaults public EXCEPT the time zone.
 *
 * The wording is short and the same under every field - "Show on public page" / "Kept private"
 * (the owner, 2026-10-19: the longer "<field> is shown on your public page" ran onto two lines in
 * a half-width column). The switch sits under its field, so the eye has the field's name; a screen
 * reader has it from the switch's own name, which carries `label`.
 */
export function VisibilitySwitch({
  checked,
  onChange,
  disabled,
  label,
}: {
  readonly checked: boolean;
  readonly onChange: (next: boolean) => void;
  readonly disabled?: boolean;
  readonly label: string;
}): ReactNode {
  const { t } = useTranslation('application');
  return (
    <Switch
      size="xs"
      checked={checked}
      // The short words on screen; the field's name in what a screen reader hears.
      aria-label={
        checked
          ? t('fields.visibility.onNamed', { label })
          : t('fields.visibility.offNamed', { label })
      }
      {...defined({ disabled })}
      onChange={(event) => {
        onChange(event.currentTarget.checked);
      }}
      label={
        <Text size="xs" c="dimmed">
          {visibilityWords(checked, t)}
        </Text>
      }
    />
  );
}

/** What a visibility switch says, on and off - every switch on the profile page says these. */
export function visibilityWords(isPublic: boolean, t: TFunction<'application'>): string {
  return isPublic ? t('fields.visibility.on') : t('fields.visibility.off');
}
